import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../app/auth';
import { Button, Input } from '../../../components/ui';
import { Eye, EyeOff, User, Lock, Settings, Download } from 'lucide-react';
import { UserRole } from '../../../types';
import { isNativePlatform } from '../../../utils/platform';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codigoRequerido, setCodigoRequerido] = useState(false);
  const [codigoDinamico, setCodigoDinamico] = useState('');
  const [aviso, setAviso] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Cargar credenciales guardadas solo en plataforma nativa Android
  useEffect(() => {
    if (!isNativePlatform()) return;
    try {
      const savedCreds = localStorage.getItem('tiempos_native_creds');
      if (savedCreds) {
        const { u, p } = JSON.parse(savedCreds);
        if (u) setUsername(u);
        if (p) setPassword(p);
        setRememberMe(true);
      }
    } catch {
      // Silenciar error de parsing
    }
  }, []);

  useEffect(() => {
    const msg = sessionStorage.getItem('tiempos_login_aviso');
    if (msg) {
      setAviso(msg);
      sessionStorage.removeItem('tiempos_login_aviso');
    }
  }, []);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;

  const getRoleRedirect = (role: UserRole): string => {
    const redirects: Record<UserRole, string> = {
      ADMIN: '/admin',
      BANCA: '/banca',
      LOCAL: '/local',
      VENDEDOR_CON_CAJA: '/local/pos',
      VENDEDOR_SIN_CAJA: '/local/pos',
      VENDEDOR: '/local/pos',
      SUPERVISOR: '/supervisor',
      BANQUERO: '/banquero',
    };
    return redirects[role];
  };

  // Valida que el "from" guardado sea accesible para el rol actual
  const getValidatedRedirect = (from: string | undefined, role: UserRole): string => {
    const roleAllowedPrefixes: Record<UserRole, string[]> = {
      ADMIN: ['/admin'],
      BANCA: ['/banca'],
      LOCAL: ['/local'],
      VENDEDOR_CON_CAJA: ['/local'],
      VENDEDOR_SIN_CAJA: ['/local'],
      VENDEDOR: ['/local'],
      SUPERVISOR: ['/supervisor'],
      BANQUERO: ['/banquero'],
    };
    const allowed = roleAllowedPrefixes[role] ?? [];
    if (from && allowed.some(prefix => from.startsWith(prefix))) {
      return from;
    }
    return getRoleRedirect(role);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await login({ username, password }, codigoRequerido ? codigoDinamico.trim() : undefined);

      if (result.codigoRequerido) {
        setCodigoRequerido(true);
        setError(result.error || 'Ingrese el código dinámico mostrado por banca.');
        return;
      }

      if (result.ok) {
        // Guardar credenciales en Android nativo si "Recordar" está activado
        if (isNativePlatform()) {
          if (rememberMe) {
            try {
              localStorage.setItem('tiempos_native_creds', JSON.stringify({ u: username, p: password }));
            } catch { /* ignore */ }
          } else {
            localStorage.removeItem('tiempos_native_creds');
          }
        }

        // Obtener el usuario para saber a dónde redirigir
        const stored = localStorage.getItem('tiempos_auth_user');
        if (stored) {
          const user = JSON.parse(stored);
          // LOCAL propio va directo a premios
          if (user.role === 'LOCAL' && user.esPropio) {
            navigate('/local/premios', { replace: true });
          } else {
            const redirect = getValidatedRedirect(from, user.role);
            navigate(redirect, { replace: true });
          }
        }
      } else {
        setError(result.error || 'Usuario o contraseña incorrectos');
      }
    } catch {
      setError('Error al iniciar sesión');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-5 sm:p-8">
      {/* Logo */}
      <div className="text-center mb-8">
        <div className="mx-auto mb-4 w-28 h-28 sm:w-44 sm:h-44">
          <img
            src="/logo-soni.png"
            alt="Logo Grupo Soni"
            className="w-full h-full object-contain drop-shadow-2xl"
            onError={(e) => { (e.target as HTMLImageElement).src = '/logo-soni-b.png'; }}
          />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-wide">GRUPO SONI</h1>
        <p className="text-gray-600 mt-1">Especialistas en ventas de loterías</p>
        {isNativePlatform() && (
          <div className="mt-3 flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/servidor')}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <Settings size={14} />
              Servidor
            </button>
          </div>
        )}
      </div>

      {/* Form */}
      {aviso && (
        <div className="bg-blue-50 border border-blue-300 text-blue-800 px-4 py-3 rounded-lg text-sm">
          {aviso}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <Input
          label="Usuario"
          type="text"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setCodigoRequerido(false);
            setCodigoDinamico('');
          }}
          placeholder="Ingrese su usuario"
          leftIcon={<User size={18} />}
          autoComplete="username"
          required
        />

        <div className="relative">
          <Input
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setCodigoRequerido(false);
              setCodigoDinamico('');
            }}
            placeholder="Ingrese su contraseña"
            leftIcon={<Lock size={18} />}
            autoComplete="current-password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] p-2 flex items-center justify-center"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {error && (
          <div className="bg-danger-50 text-danger-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {codigoRequerido && (
          <Input
            label="Código dinámico"
            type="text"
            value={codigoDinamico}
            onChange={(e) => setCodigoDinamico(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Ingrese código de 6 dígitos"
            maxLength={6}
            required
          />
        )}

        {isNativePlatform() && (
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-600">Recordar credenciales</span>
          </label>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={isSubmitting || isLoading}
        >
          Iniciar Sesión
        </Button>
      </form>

      {/* Botón descarga APK — solo visible en web móvil */}
      {!isNativePlatform() && (
        <a
          href="https://www.gruposoii.com/apk/tiemposb.apk"
          download
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors sm:hidden"
        >
          <Download size={18} />
          Descargar APK
        </a>
      )}
    </div>
  );
};

export default LoginPage;
